import { useEffect, useMemo, useState } from 'react';
import { db, reference, type LocalInvoice, type LocalStop } from '../lib/localdb.js';
import { addCapture, proposedTime, recordPayment, requestReturn, saveAccess, transition } from '../lib/actions.js';
import { apiJson, ApiError } from '../lib/api.js';
import { amount, minutes, text } from '../lib/format.js';
import type { Checklist } from '../lib/stop.js';
import { BigButton, Chip, Keypad, Sheet, SignaturePad } from '../components/Controls.js';
import { QuoteSheet } from './Quote.js';
import { DebriefSheet } from './Debrief.js';
import { encodeQr, qrSvgPath } from '../lib/qr.js';

/**
 * Finishing a stop.
 *
 * *Done* is where the visit says what it was, and it is never a claim about the job. Four
 * outcomes, because the first trip diagnoses and the second installs the part that had to be
 * ordered:
 *
 *   - **Complete** — the work planned for this visit is done.
 *   - **Partial** — work began and stopped. Partial work, partial time, an invoicing question
 *     for the office. Carries a reason.
 *   - **Return visit needed** — a *request* the office schedules. The technician has no
 *     `job.write` and does not book.
 *   - **Could not do** — nothing was done; that lives on its own panel with the reason list.
 *
 * Then the paperwork, in the order it actually happens at a door: the hours the transitions
 * imply, the chips for whoever comes next, the signature, and only then the money.
 */

type Requirement = { id: string; name: unknown; equipmentNote: unknown; isHeight: boolean; isConfinedSpace: boolean };
type Settings = { holdChequesPending?: boolean };

export function FinishPanel({
  stop,
  checklist,
  capabilities,
  onDone,
}: {
  stop: LocalStop;
  checklist: Checklist | null;
  capabilities: Set<string>;
  onDone: () => void;
}) {
  const proposed = proposedTime(stop);
  const [outcome, setOutcome] = useState<'complete' | 'partial' | 'return_needed'>('complete');
  const [travel, setTravel] = useState(proposed.travelMinutes);
  const [onSite, setOnSite] = useState(proposed.onSiteMinutes);
  const [note, setNote] = useState('');
  const [crewMinutes, setCrewMinutes] = useState<Map<string, number>>(new Map());
  const [chips, setChips] = useState<Set<string>>(new Set());
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [signatureId, setSignatureId] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [returnReason, setReturnReason] = useState<'parts' | 'time' | 'second_person' | 'customer' | 'other'>('parts');
  const [invoices, setInvoices] = useState<LocalInvoice[]>([]);
  const [payOpen, setPayOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  /*
   * Offered after the stop closes rather than before it, because a debrief is about the job
   * just left. Leaving is the moment it is freshest, and the moment the technician has a hand
   * free to press one large control while still stationary.
   */
  const [debriefOpen, setDebriefOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      setRequirements(await reference<Requirement[]>('accessRequirements', []));
      const database = await db();
      setInvoices((await database.getAllFromIndex('invoices', 'byJob', stop.job.id)) ?? []);
      if (stop.address) {
        const place = await database.get('places', stop.address.placeKey);
        setChips(new Set((place?.access ?? []).map((chip) => chip.requirementId)));
      }
    })();
  }, [stop.id, stop.job.id, stop.address?.placeKey]);

  const outstanding = checklist?.outstanding ?? [];
  const owing = invoices.filter((invoice) => Number(invoice.balance) > 0);

  const finish = async () => {
    setBusy(true);
    try {
      // The chips first: *what should the next person bring?* is answered while the technician
      // is still standing in front of the answer.
      await saveAccess(
        stop.id,
        [...chips].map((requirementId) => ({ requirementId })),
      );

      await transition(stop.id, 'complete', {
        complete: {
          outcome,
          note: note.trim() || null,
          time: {
            travelMinutes: travel,
            onSiteMinutes: onSite,
            crew: [...crewMinutes.entries()].map(([userId, mins]) => ({ userId, minutes: mins })),
          },
          request:
            outcome === 'return_needed'
              ? { reasonKey: returnReason, note: note.trim() || null, needsSecondPerson: returnReason === 'second_person' }
              : undefined,
        },
      });
      setDebriefOpen(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="finish">
      {/* What is still owed before this can honestly be called done. */}
      {outstanding.length > 0 && (
        <section className="finish__block finish__block--warn" role="alert">
          <h3>Il reste</h3>
          <ul>
            {outstanding.map((requirement) => (
              <li key={requirement.id}>{requirement.title}</li>
            ))}
          </ul>
          <p className="quiet">Vous pouvez terminer quand même ; l’office verra ce qui manque.</p>
        </section>
      )}

      <section className="finish__block">
        <h3>Comment ça s’est terminé</h3>
        <div className="chips">
          <Chip tone="positive" on={outcome === 'complete'} onToggle={() => setOutcome('complete')}>
            Travail complété
          </Chip>
          <Chip tone="warm" on={outcome === 'partial'} onToggle={() => setOutcome('partial')}>
            Partiel
          </Chip>
          <Chip tone="warm" on={outcome === 'return_needed'} onToggle={() => setOutcome('return_needed')}>
            Retour nécessaire
          </Chip>
        </div>

        {outcome === 'return_needed' && (
          <>
            <h4>Pourquoi un retour</h4>
            <div className="chips">
              {(
                [
                  ['parts', 'Pièces à commander'],
                  ['time', 'Plus de temps que la plage'],
                  ['second_person', 'Deuxième personne'],
                  ['customer', 'Client encore en réflexion'],
                  ['other', 'Autre'],
                ] as const
              ).map(([key, label]) => (
                <Chip key={key} on={returnReason === key} onToggle={() => setReturnReason(key)}>
                  {label}
                </Chip>
              ))}
            </div>
            <p className="quiet">
              L’office reçoit une demande à planifier. Vous ne fixez pas la date : ce n’est pas votre rôle.
            </p>
          </>
        )}

        <label className="field">
          <span>Note{outcome === 'partial' ? ' (ce qui reste)' : ''}</span>
          <textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
        </label>
      </section>

      {/*
        Time is derived, not clocked.
        The transitions are the timer, and both spans are proposed for confirmation with one
        tap. This is what removes the runaway clock structurally: twenty-five imported entries
        exceed sixteen hours, and a clock that ends when the stop ends cannot do that.
      */}
      <section className="finish__block">
        <h3>Heures</h3>
        <MinuteRow label="Déplacement" value={travel} onChange={setTravel} />
        <MinuteRow label="Sur place" value={onSite} onChange={setOnSite} />

        {/*
          The crew's hours, logged by the lead — including a helper without a phone, who is
          otherwise invisible. Shown only to somebody who may write them.
        */}
        {capabilities.has('time.write.crew') && stop.crew.length > 1 && (
          <>
            <h4>Équipe présente</h4>
            {stop.crew.map((member) => (
              <MinuteRow
                key={member.id}
                label={member.name}
                value={crewMinutes.get(member.id) ?? onSite}
                onChange={(value) =>
                  setCrewMinutes((current) => {
                    const next = new Map(current);
                    next.set(member.id, value);
                    return next;
                  })
                }
              />
            ))}
          </>
        )}
      </section>

      {/* One tap, and the record becomes right over time without anyone filling in a form. */}
      <section className="finish__block">
        <h3>Que devrait apporter la prochaine personne ?</h3>
        <div className="chips">
          {requirements.map((requirement) => (
            <Chip
              key={requirement.id}
              tone={requirement.isHeight || requirement.isConfinedSpace ? 'warm' : 'neutral'}
              on={chips.has(requirement.id)}
              onToggle={() =>
                setChips((current) => {
                  const next = new Set(current);
                  if (next.has(requirement.id)) next.delete(requirement.id);
                  else next.add(requirement.id);
                  return next;
                })
              }
            >
              {text(requirement.name)}
              {requirement.equipmentNote ? ` · ${text(requirement.equipmentNote)}` : ''}
            </Chip>
          ))}
        </div>
      </section>

      {/* Work acceptance: the customer confirms the work described was done. */}
      <section className="finish__block">
        <h3>Acceptation des travaux</h3>
        {signatureId ? (
          <p className="quiet">Signé.</p>
        ) : signing ? (
          <SignaturePad
            label={`${stop.customer.name} — acceptation des travaux`}
            onDone={async (blob) => {
              const saved = await addCapture({
                appointmentId: stop.id,
                kind: 'signature',
                blob,
                caption: `Acceptation — ${stop.customer.name}`,
                withPosition: true,
              });
              setSignatureId(saved.id);
              setSigning(false);
            }}
          />
        ) : (
          <BigButton tone="quiet" onClick={() => setSigning(true)}>
            Faire signer le client
          </BigButton>
        )}
      </section>

      <DocumentsBlock
        stop={stop}
        capabilities={capabilities}
        owing={owing}
        onPay={() => setPayOpen(true)}
        onQuote={() => setQuoteOpen(true)}
      />

      <BigButton tone="positive" onClick={() => void finish()} busy={busy} hint={`${minutes(travel + onSite)} au total`}>
        Terminer l’arrêt
      </BigButton>

      <PaymentSheet
        open={payOpen}
        stop={stop}
        invoices={owing}
        onClose={() => setPayOpen(false)}
        onSaved={() => setPayOpen(false)}
      />

      <QuoteSheet open={quoteOpen} stop={stop} onClose={() => setQuoteOpen(false)} />

      {/* The last thing before pulling out, and the reason the recorder exists. */}
      <DebriefSheet
        open={debriefOpen}
        stopId={stop.id}
        customerName={stop.customer.name}
        onClose={() => {
          setDebriefOpen(false);
          onDone();
        }}
      />
    </div>
  );
}

function MinuteRow({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return (
    <div className="minutes">
      <span className="minutes__label">{label}</span>
      <button type="button" onClick={() => onChange(Math.max(0, value - 15))} aria-label={`${label} moins 15 minutes`}>
        −
      </button>
      <span className="minutes__value tnum">{minutes(value)}</span>
      <button type="button" onClick={() => onChange(value + 15)} aria-label={`${label} plus 15 minutes`}>
        +
      </button>
    </div>
  );
}

/**
 * The report and the invoice.
 *
 * Both assemble themselves from what the stop recorded — that is the point of having recorded
 * any of it — and both are **held until cutover**. Nothing reaches a real customer while this
 * is a development system holding live client data, so the client shows them as *retenu*
 * rather than as sent. This is the one feature that is fully built and deliberately not
 * connected.
 */
function DocumentsBlock({
  stop,
  capabilities,
  owing,
  onPay,
  onQuote,
}: {
  stop: LocalStop;
  capabilities: Set<string>;
  owing: LocalInvoice[];
  onPay: () => void;
  onQuote: () => void;
}) {
  const [drafting, setDrafting] = useState(false);
  const [result, setResult] = useState<{ number: string; total: string; unpriced: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A control the account cannot use is a question the technician has to ask somebody about.
  const mayDraft = capabilities.has('billing.draft.own') || capabilities.has('billing.write');

  const draft = async () => {
    setDrafting(true);
    setError(null);
    try {
      const invoice = await apiJson<{ number: string; total: string; unpriced: string[] }>('/invoices', {
        method: 'POST',
        headers: { 'idempotency-key': crypto.randomUUID() },
        body: JSON.stringify({ jobId: stop.job.id, appointmentId: stop.id, assemble: true }),
      });
      setResult(invoice);
    } catch (caught) {
      /*
       * Drafting needs the network, and says so.
       *
       * Deliberately NOT queued: a document assembled from the stop has to be priced from the
       * catalogue by the server, and a draft the technician cannot see the total of is worse
       * than one they raise from the van afterwards.
       */
      setError(caught instanceof ApiError ? caught.message : 'Sans signal, la facture se prépare au retour.');
    } finally {
      setDrafting(false);
    }
  };

  return (
    <section className="finish__block">
      <h3>Documents</h3>

      {mayDraft ? (
        <>
          <BigButton tone="quiet" onClick={() => void draft()} busy={drafting}>
            Préparer la facture
          </BigButton>
          {/*
            A hazard or a defect is the best justification a quote will ever have, and the
            technician who photographed it is standing there. Only offered to a role the
            business opted in.
          */}
          <BigButton tone="quiet" onClick={onQuote}>
            Soumission pour la réparation
          </BigButton>
          {result && (
            <p className="quiet">
              Brouillon {result.number} · {amount(result.total)}
              {result.unpriced.length > 0 && ` · à chiffrer par l’office : ${result.unpriced.join(', ')}`}
            </p>
          )}
          {error && <p className="warn">{error}</p>}
        </>
      ) : (
        <p className="quiet">La facture est préparée par l’office.</p>
      )}

      <ReportBlock stop={stop} />

      {owing.length > 0 && capabilities.has('payment.collect') && (
        <BigButton tone="warm" onClick={onPay}>
          Percevoir {amount(owing.reduce((total, invoice) => total + Number(invoice.balance), 0).toFixed(2))}
        </BigButton>
      )}
    </section>
  );
}

/**
 * Taking money at the door.
 *
 * **The technician's phone never takes a card.** No number is typed into it or read by it,
 * which keeps PCI scope, card-not-present rates and hardware terminals out of the PWA
 * entirely. The customer pays on their own phone from a QR code, and the payment appears here
 * at the next sync — when the processor has told the server, and not before.
 *
 * Cash and cheque are inserts and queue like anything else. An e-transfer is a promise: it is
 * recorded `pending`, and the office confirms receipt.
 */
function PaymentSheet({
  open,
  stop,
  invoices,
  onClose,
  onSaved,
}: {
  open: boolean;
  stop: LocalStop;
  invoices: LocalInvoice[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [method, setMethod] = useState<'cash' | 'check' | 'e_transfer' | 'link'>('link');
  const [value, setValue] = useState('');
  const [link, setLink] = useState<{ url: string; live: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<Settings>({});

  useEffect(() => {
    if (open) void reference<Settings>('settings', {}).then(setSettings);
  }, [open]);

  const invoice = invoices[0] ?? null;
  const numeric = Number(value.replace(',', '.'));

  const makeLink = async () => {
    if (!invoice) return;
    setError(null);
    try {
      const created = await apiJson<{ url: string; live: boolean }>(`/invoices/${invoice.id}/payment-link`, {
        method: 'POST',
      });
      setLink(created);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Le lien de paiement demande du signal.');
    }
  };

  return (
    <Sheet open={open} title="Paiement" onClose={onClose}>
      <div className="chips">
        <Chip on={method === 'link'} onToggle={() => setMethod('link')}>
          Carte — code QR
        </Chip>
        <Chip on={method === 'cash'} onToggle={() => setMethod('cash')}>
          Comptant
        </Chip>
        <Chip on={method === 'check'} onToggle={() => setMethod('check')}>
          Chèque
        </Chip>
        <Chip on={method === 'e_transfer'} onToggle={() => setMethod('e_transfer')}>
          Virement Interac
        </Chip>
      </div>

      {method === 'link' ? (
        <>
          <p className="quiet">
            Le client paie sur son propre téléphone. Aucune donnée de carte ne passe par cet appareil.
          </p>
          {link ? (
            link.live ? (
              <>
                <QrCode value={link.url} />
                <p className="quiet">
                  Le paiement apparaîtra ici quand le processeur l’aura confirmé — pas avant.
                </p>
              </>
            ) : (
              <p className="warn">Paiement en ligne non configuré. Utilisez comptant, chèque ou virement.</p>
            )
          ) : (
            <BigButton onClick={() => void makeLink()} disabled={!invoice}>
              Afficher le code QR
            </BigButton>
          )}
          {error && <p className="warn">{error}</p>}
        </>
      ) : (
        <>
          <p className="reading__value tnum">{value || '—'} $</p>
          <Keypad value={value} onChange={setValue} />
          {method === 'e_transfer' && (
            <p className="quiet">Enregistré comme <strong>en attente</strong> : l’argent arrive plus tard.</p>
          )}
          {method === 'check' && settings.holdChequesPending && (
            <p className="quiet">Conservé en attente jusqu’au dépôt.</p>
          )}
          <BigButton
            tone="positive"
            disabled={!Number.isFinite(numeric) || numeric <= 0}
            onClick={async () => {
              await recordPayment(stop.id, {
                customerId: stop.customer.id,
                invoiceId: invoice?.id ?? null,
                method,
                amount: numeric.toFixed(2),
              });
              onSaved();
            }}
          >
            Enregistrer le paiement
          </BigButton>
        </>
      )}
    </Sheet>
  );
}

/**
 * A QR code, drawn on the device.
 *
 * It cannot be fetched: the technician's phone is usually the one without signal, and the
 * whole point of the link is that the **customer's** phone has one. `lib/qr.ts` is a complete
 * encoder for exactly this reason.
 *
 * Drawn light-on-dark would be wrong however well it matched the surface — scanners expect
 * dark modules on a light ground, and a code that needs a second attempt in a driveway is a
 * code that gets abandoned. So this one panel is deliberately the wrong colour for the app.
 */
function QrCode({ value }: { value: string }) {
  const matrix = useMemo(() => {
    try {
      return encodeQr(value);
    } catch {
      return null;
    }
  }, [value]);

  if (!matrix) return <p className="qr__value tnum">{value}</p>;

  const quiet = 4; // The mandatory quiet zone. Without it a scanner may not find the code.
  const span = matrix.size + quiet * 2;

  return (
    <div className="qr">
      <svg viewBox={`0 0 ${span} ${span}`} width="100%" role="img" aria-label="Code QR de paiement">
        <rect width={span} height={span} fill="#ffffff" />
        <g transform={`translate(${quiet} ${quiet})`} fill="#000000">
          <path d={qrSvgPath(matrix)} />
        </g>
      </svg>
      <p className="qr__hint">Le client scanne et paie sur son téléphone.</p>
    </div>
  );
}

/**
 * The work report.
 *
 * It exists without anyone writing it — what was found, what was done, the parts, the
 * nameplate, the acceptance signature — because the captures are typed and the readings are
 * numbers. It is the document a service company is actually judged on, and before this it did
 * not exist anywhere.
 *
 * **It is held, and it says so.** Nothing reaches a real customer while this is a development
 * system holding live client data; the send is genuinely built, genuinely runs, and lands in
 * `dev_outbox`. The one rule the client must never break is telling a technician a customer
 * has been sent something before the server says it left.
 */
function ReportBlock({ stop }: { stop: LocalStop }) {
  const [preview, setPreview] = useState<string | null>(null);
  const [state, setState] = useState<'idle' | 'held' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    setError(null);
    try {
      const report = await apiJson<{ text: string }>(`/appointments/${stop.id}/report`);
      setPreview(report.text);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Le rapport se prépare au retour du signal.');
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await apiJson<{ delivered: boolean; held: boolean }>(`/appointments/${stop.id}/report/send`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      // `delivered` is the server's confirmation and the only thing that earns the word
      // "envoyé". Anything else is held, and is shown as held.
      setState(result.delivered ? 'sent' : 'held');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Envoi impossible sans signal.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <BigButton tone="quiet" onClick={() => void load()} busy={busy}>
        Voir le rapport
      </BigButton>

      {preview && (
        <>
          <pre className="report" lang={stop.customer.locale}>
            {preview}
          </pre>
          <BigButton tone="quiet" onClick={() => void send()} busy={busy}>
            Transmettre le rapport
          </BigButton>
        </>
      )}

      {state === 'held' && (
        <p className="held">
          Rapport <strong>retenu</strong> — rien n’est transmis au client tant que le système n’est pas en service.
        </p>
      )}
      {state === 'sent' && <p className="positive">Rapport transmis.</p>}
      {error && <p className="warn">{error}</p>}
    </>
  );
}
